import React from "react";
import RichTextField from "components/Contentful/RichTextField";
import styled, { useTheme, css } from "styled-components";
import Button from "components/Button";
import Link from "next/link";
import PropTypes from "prop-types";
import Image from "next/image";
import { setThemeColor } from "lib/theme-color-helpers";
import useCustomerUser from "hooks/use-customer-user";
import LogoutButtonLink from "components/ActionItem/LogoutButtonLink";
import { LinkWrapper } from "components/Shared";

const OuterWrapper = styled.section`
  background-color: ${({ theme, backgroundColor }) =>
    backgroundColor || theme.themeColors.white};
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  padding: 3.8125rem 1.5rem;
  position: relative;
  width: 100%;

  ${({ textAlignment }) =>
    textAlignment !== "center" &&
    css`
      &:nth-child(even) {
        padding: 0 1.5rem 3.8125rem;
      }
    `};

  ${({ theme }) => theme.mq.sm`
    padding: 4.5rem 1.5rem;

    ${({ textAlignment }) =>
      textAlignment !== "center" &&
      css`
        &:nth-child(even) {
          padding: 0 1.5rem 4.5rem;
        }
      `};
  `};

  ${({ theme }) => theme.mq.xl`
    align-items: center;
    flex-direction: ${({ imageAlignment }) =>
      (imageAlignment === "center" && "column") || "row"};
    justify-content: center;
    padding: 5.125rem 1.5rem;

    ${({ textAlignment }) =>
      textAlignment !== "center" &&
      css`
        &:nth-child(even) {
          padding: 0 1.5rem 5.25rem;
        }
      `};
  `};
`;

const ImageWrapper = styled.div`
  align-self: center;
  display: inline-block;
  margin: 0 auto 4rem;
  max-width: 37.5rem;
  order: 0;
  width: 100%;

  ${({ imageAlignment }) =>
    imageAlignment === "center" &&
    css`
      margin: 2rem -1.5rem -5rem;
      width: calc(100vw + 3rem);
      max-width: calc(100vw + 3rem);
      order: 1;
    `};

  ${({ theme }) => theme.mq.sm`
    ${({ imageAlignment }) =>
      imageAlignment === "center" &&
      css`
        margin: 3rem -1.5rem -4.5rem;
      `};
  `};

  ${({ theme }) => theme.mq.xl`
    margin: 0;
    order: ${({ imageAlignment }) => (imageAlignment === "left" && "0") || "1"};

    ${({ imageAlignment }) =>
      imageAlignment === "center" &&
      css`
        margin: 3rem -1.5rem -5.125rem;
      `};
  `};
`;

const ImageWrapperMobile = styled.div`
  ${({ theme }) => theme.mq.lg`
    display: none;
  `}
`;

const ImageWrapperDesktop = styled.div`
  display: none;

  ${({ theme }) => theme.mq.lg`
    display: block;
  `}
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  max-width: ${({ imageAlignment }) =>
    (imageAlignment === "center" && "47.5rem") || "37.5rem"};
  padding: 0;
  text-align: ${({ textAlignment }) => textAlignment};
  width: 100%;

  ${({ theme }) => theme.mq.xl`
    margin: 0;
    max-width: ${({ imageAlignment }) =>
      (imageAlignment === "center" && "47.5rem") || "37.5rem"};
    padding-bottom: 0;
    padding-left: ${({ imageAlignment }) =>
      (imageAlignment === "left" && "4rem") || "0"};
    padding-right: ${({ imageAlignment }) =>
      (imageAlignment === "right" && "3rem") || "0"};
    padding-top: 0;
    order: ${({ imageAlignment }) => (imageAlignment === "left" && "1") || "0"};
    width: ${({ imageAlignment }) =>
      (imageAlignment === "center" && "47.5rem") || "50%"};
  `};
`;

const ButtonWrapper = styled.a`
  display: flex;
  margin: 2rem auto;
  width: 100%;

  ${({ theme }) => theme.mq.sm`
    max-width: 12.5625rem;
  `}
`;

const StyledLinkWrapper = styled(LinkWrapper)`
  align-self: center;
`;

const ImageTextBlock = ({ blockData }) => {
  const theme = useTheme();
  const backgroundColor = blockData?.backgroundColor;
  const backgroundThemeColor =
    theme.themeColors[setThemeColor(backgroundColor)];
  const image = blockData?.image;
  const imageMobile = blockData?.imageMobile;
  const imageMobileUrl = imageMobile?.fields?.file?.url;
  const imageMobileAltText = imageMobile?.fields?.title;
  const imageMobileHeight = imageMobile?.fields?.file?.details?.image?.height;
  const imageMobileWidth = imageMobile?.fields?.file?.details?.image?.width;
  const imageUrl = image?.fields?.file?.url;
  const imageAltText = image?.fields?.title;
  const imageHeight = image?.fields?.file?.details?.image?.height;
  const imageWidth = image?.fields?.file?.details?.image?.width;
  const imageAlignment = blockData?.imageAlignment;
  const content = blockData?.content;
  const textAlignment = blockData?.textAlignment;
  const buttonLink = blockData?.buttonLink;
  const buttonText = blockData?.buttonText;
  const customerUser = useCustomerUser();
  const providerSignUpLink = "/provider/auth/sessionless";
  return (
    <>
      <OuterWrapper
        backgroundColor={backgroundThemeColor}
        imageAlignment={imageAlignment}
        textAlignment={textAlignment}
      >
        <ImageWrapper imageAlignment={imageAlignment}>
          {imageMobile && (
            <ImageWrapperMobile>
              <Image
                alt={imageMobileAltText}
                height={imageMobileHeight}
                layout="responsive"
                priority
                src={`https:${imageMobileUrl}`}
                width={imageMobileWidth}
              />
            </ImageWrapperMobile>
          )}
          {image && (
            <ImageWrapperDesktop>
              <Image
                alt={imageAltText}
                height={imageHeight}
                layout="responsive"
                priority
                src={`https:${imageUrl}`}
                width={imageWidth}
              />
            </ImageWrapperDesktop>
          )}
        </ImageWrapper>
        <ContentWrapper
          imageAlignment={imageAlignment}
          textAlignment={textAlignment}
        >
          {content && (
            <RichTextField
              richText={content}
              textAlignment={textAlignment}
              textColor={
                (backgroundColor === "orange" && "white") || "darkGreen1"
              }
              backgroundColor={backgroundColor}
            />
          )}
          {buttonLink !== providerSignUpLink && buttonLink && (
            <Link href={buttonLink} passHref>
              <ButtonWrapper>
                <Button colorType="lightGreen3" textColor="primary">
                  {buttonText}
                </Button>
              </ButtonWrapper>
            </Link>
          )}
          {buttonLink === providerSignUpLink && !customerUser && buttonLink ? (
            <Link href={buttonLink} passHref>
              <ButtonWrapper>
                <Button colorType="lightGreen3" textColor="primary">
                  {buttonText}
                </Button>
              </ButtonWrapper>
            </Link>
          ) : (
            <StyledLinkWrapper>
              <LogoutButtonLink
                bgColor={theme.themeColors.lightGreen3}
                color={theme.themeColors.primary}
                fontSize="16px"
                textContent={buttonText}
                redirectUrl={buttonLink}
                isButton
              />
            </StyledLinkWrapper>
          )}
        </ContentWrapper>
      </OuterWrapper>
    </>
  );
};

export default ImageTextBlock;

ImageTextBlock.propTypes = {
  blockData: PropTypes.shape({
    backgroundColor: PropTypes.string,
    content: PropTypes.object,
    imageMobile: PropTypes.object,
    image: PropTypes.object,
    imageAlignment: PropTypes.string,
    textAlignment: PropTypes.string,
    buttonLink: PropTypes.string,
    buttonText: PropTypes.string,
  }),
};

ImageTextBlock.defaultProps = {
  blockData: {
    backgroundColor: "",
    content: {},
    imageMobile: {},
    image: {},
    imageAlignment: "left",
    textAlignment: "left",
    buttonLink: "",
    buttonText: "",
  },
};
